import React from "react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";

import type { TranslationTopic } from "../models";
import topicService from "../services/topicService";
import { Loader } from "./Loader";

type TopicsDialogProps = {
  selectedTopicId?: string | null;
  onTopicsChanged?: (topics: TranslationTopic[]) => void;
};

@observer
export default class TopicsDialog extends React.Component<TopicsDialogProps> {
  @observable private _topics: TranslationTopic[] = [];
  @observable private _loading = true;
  @observable private _busy = false;
  @observable private _editingId: string | null = null; // null = creating new
  @observable private _title = '';
  @observable private _text = '';

  constructor(props: TopicsDialogProps) {
    super(props);
    makeObservable(this);
  }

  async componentDidMount() {
    await this._loadTopics();
  }

  render() {
    const isEditing = this._editingId !== null;
    const canSave = !this._busy && this._title.trim().length > 0 && this._text.trim().length > 0;

    return (
      <>
        <div className="topics-dialog-hint">
          A selected topic is sent with every translation as extra context for the model.
        </div>
        <div className="form-group">
          <label className="form-label">{isEditing ? 'Edit topic' : 'New topic'}</label>
          <input
            className="form-input"
            type="text"
            placeholder="Title (e.g. Harry Potter)"
            value={this._title}
            maxLength={100}
            onChange={this._onTitleChange}
          />
        </div>
        <div className="form-group">
          <textarea
            className="form-input topics-dialog-textarea"
            placeholder="Topic description / context (e.g. fantasy novel, British English, wizarding world terminology)"
            value={this._text}
            maxLength={2000}
            rows={4}
            onChange={this._onTextChange}
          />
        </div>
        <div className="form-group topics-dialog-actions">
          <button className="btn btn-primary" disabled={!canSave} onClick={() => void this._onSaveClick()}>
            {this._busy && <Loader size="xsm" />}
            {isEditing ? 'Save changes' : 'Add topic'}
          </button>
          {isEditing && (
            <button className="btn btn-secondary" disabled={this._busy} onClick={this._onNewClick}>
              New
            </button>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Your topics</label>
          {this._loading ? <div className="topics-dialog-loading"><Loader size="sm" /></div>
            : this._topics.length === 0 ? <div className="topics-dialog-empty">No topics yet — add one above.</div>
            : (
              <div className="topics-list">
                {this._topics.map(topic => (
                  <div
                    key={topic.id}
                    className={'topics-list-item' + (topic.id === this._editingId ? ' topics-list-item-editing' : '') + (topic.id === this.props.selectedTopicId ? ' topics-list-item-selected' : '')}
                    onClick={() => this._onEditClick(topic)}
                  >
                    <div className="topics-list-item-title">
                      {topic.title}
                      {topic.id === this.props.selectedTopicId && <span className="topics-list-item-badge">in use</span>}
                    </div>
                    <div className="topics-list-item-text">{topic.text}</div>
                    <button
                      className="btn btn-secondary topics-list-item-delete"
                      title="Delete topic"
                      onClick={(e) => { e.stopPropagation(); void this._onDeleteClick(topic); }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
        </div>
      </>
    );
  }

  @action private async _loadTopics() {
    this._loading = true;
    try {
      const topics = await topicService.listTopics();
      this._topics = topics;
      this.props.onTopicsChanged?.(topics);
    } finally {
      this._loading = false;
    }
  }

  @action _onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this._title = e.target.value;
  };

  @action _onTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this._text = e.target.value;
  };

  @action _onNewClick = () => {
    this._editingId = null;
    this._title = '';
    this._text = '';
  };

  @action _onEditClick = (topic: TranslationTopic) => {
    this._editingId = topic.id;
    this._title = topic.title;
    this._text = topic.text;
  };

  @action private async _onSaveClick() {
    const title = this._title.trim();
    const text = this._text.trim();
    if (!title || !text) {
      return;
    }
    this._busy = true;
    try {
      if (this._editingId) {
        await topicService.updateTopic(this._editingId, title, text);
      } else {
        const created = await topicService.createTopic(title, text);
        this._editingId = created.id;
      }
      await this._loadTopics();
    } finally {
      this._busy = false;
    }
  }

  @action private async _onDeleteClick(topic: TranslationTopic) {
    this._busy = true;
    try {
      await topicService.deleteTopic(topic.id);
      if (this._editingId === topic.id) {
        this._onNewClick();
      }
      await this._loadTopics();
    } finally {
      this._busy = false;
    }
  }
}
